import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import datapricing from "../assets/datapricing.svg";
import { apiRequest } from "../api/client";
import privacy from "../assets/privacy.svg";

const PAGE_MODULE = "privacy_policy";

function normalizeText(v) {
  if (v === null || v === undefined) return "";
  return String(v).replace(/\r\n/g, "\n").trim();
}

function isHttpUrl(url) {
  if (!url) return false;
  const s = String(url);
  return s.startsWith("http://") || s.startsWith("https://");
}

function SectionIcon({ title, sizeClass = "h-8 w-8" }) {
  const key = String(title || "").toLowerCase();

  if (key.includes("account")) {
    return (
      <svg viewBox="0 0 24 24" className={`${sizeClass} text-emerald-500`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
        <path d="M12 11a4 4 0 100-8 4 4 0 000 8z" />
      </svg>
    );
  }
  if (key.includes("usage")) {
    return (
      <svg viewBox="0 0 24 24" className={`${sizeClass} text-sky-500`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M3 3v18h18" />
        <path d="M7 14l3-3 3 3 5-5" />
      </svg>
    );
  }
  if (key.includes("device") || key.includes("technical")) {
    return (
      <svg
        viewBox="0 0 24 24"
        className={`${sizeClass} text-amber-500 scale-[0.86] origin-center`}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M12 18h.01" />
        <path d="M9 10h6" />
        <path d="M10 14h4" />
        <path d="M10 2h4l6 6v10a4 4 0 01-4 4H8a4 4 0 01-4-4V6a4 4 0 014-4z" />
      </svg>
    );
  }
  if (key.includes("cookies")) {
    return (
      <svg viewBox="0 0 24 24" className={`${sizeClass} text-amber-600`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M21 12a9 9 0 11-9-9 4 4 0 004 4 4 4 0 004 4 4 4 0 004-4" />
        <path d="M8 13h.01" />
        <path d="M12 17h.01" />
        <path d="M11 10h.01" />
      </svg>
    );
  }
  if (key.includes("share")) {
    return (
      <svg viewBox="0 0 24 24" className={`${sizeClass} text-sky-600`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7" />
        <path d="M16 6l-4-4-4 4" />
        <path d="M12 2v13" />
      </svg>
    );
  }
  if (key.includes("protect") || key.includes("security")) {
    return (
      <svg viewBox="0 0 24 24" className={`${sizeClass} text-emerald-600`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M12 2l7 4v6c0 5-3 9-7 10-4-1-7-5-7-10V6l7-4z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    );
  }
  if (key.includes("opt out") || key.includes("unsubscribe")) {
    return (
      <svg viewBox="0 0 24 24" className={`${sizeClass} text-sky-500`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M4 4h16v12H5a1 1 0 01-1-1V4z" />
        <path d="M22 7l-10 6L2 7" />
        <path d="M7 20h10" />
      </svg>
    );
  }
  if (key.includes("delete")) {
    return (
      <svg viewBox="0 0 24 24" className={`${sizeClass} text-rose-500`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M3 6h18" />
        <path d="M8 6V4h8v2" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M6 6l1 16h10l1-16" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className={`${sizeClass} text-slate-500`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 22a10 10 0 100-20 10 10 0 000 20z" />
      <path d="M12 8h.01" />
      <path d="M11 12h1v6h-1z" />
    </svg>
  );
}

function InfoRow({ icon, title, description, className = "" }) {
  return (
    <div className={["rounded-2xl border border-[#E6EAF0] bg-white p-4 shadow-sm sm:p-5 sm:shadow-md", className].join(" ")}>
      <div className="flex items-start gap-3 sm:items-center">
        <div className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center p-1 sm:h-16 sm:w-16">
          {icon}
        </div>
        <div className="min-w-0">
          <h3 className="text-start text-[16px] font-semibold text-[#24303A] sm:text-[18px]">{title}</h3>
          <p className="mt-1 whitespace-pre-line text-start text-[14px] leading-5 text-[#6E7B86] sm:text-[16px]">{description}</p>
        </div>
      </div>
    </div>
  );
}

function InfoRowCentered({ icon, title, description, className = "" }) {
  return (
    <div className={["rounded-2xl border border-[#E6EAF0] bg-white p-4 shadow-sm sm:p-5 sm:shadow-md", className].join(" ")}>
      <div className="flex flex-col items-center gap-3 grid-cols-[96px_1fr]">
        <div className="mt-0.5 flex h-16 w-16 items-center justify-center p-1 sm:h-20 sm:w-20">
          {icon}
        </div>
        <div className="min-w-0 text-center">
          <h3 className="text-[16px] font-semibold text-[#24303A] sm:text-[18px]">{title}</h3>
          <p className="mt-1 whitespace-pre-line text-[14px] leading-5 text-[#6E7B86] sm:text-[16px]">{description}</p>
        </div>
      </div>
    </div>
  );
}

export default function PrivacyPolicy({ onOpenAuth, onOpenMenu }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageHeader, setPageHeader] = useState(null);
  const [sections, setSections] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      const res = await apiRequest(
        `api/page_sections?page_module=${encodeURIComponent(PAGE_MODULE)}`
      );
      if (!alive) return;
      if (!res.ok) {
        setError("Failed to load privacy policy.");
        setPageHeader(null);
        setSections([]);
      } else {
        const firstHeader = Array.isArray(res.data?.page_sections) ? res.data.page_sections[0] : null;
        setPageHeader(firstHeader || null);
        setSections(Array.isArray(res.data?.grouped_sections) ? res.data.grouped_sections : []);
      }
      setLoading(false);
    })().catch(() => {
      if (!alive) return;
      setError("Failed to load privacy policy.");
      setPageHeader(null);
      setSections([]);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const featureItems = useMemo(() => {
    const group = sections.find((s) => s?.section_type === "features_data");
    return Array.isArray(group?.items) ? group.items : [];
  }, [sections]);

  const policyItems = useMemo(() => {
    const group = sections.find((s) => s?.section_type === "privacy_policy_data");
    return Array.isArray(group?.items) ? group.items : [];
  }, [sections]);

  const heroImage = isHttpUrl(pageHeader?.image_url) ? pageHeader.image_url : "";
  const heroTitle = normalizeText(pageHeader?.title) || "Privacy Policy";
  const heroSubTitle = normalizeText(pageHeader?.sub_title) || "Your trust is our priority.";
  const heroDescription =
    normalizeText(pageHeader?.description) ||
    "This page explains what we collect, why we collect it, and the choices you have.";

  return (
    <div className="min-h-screen text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto w-full max-w-7xl flex-1 ">
        <section className="overflow-hidden ">
          <div className="grid gap-5 sm:gap-6 md:grid-cols-[1.1fr_0.9fr] md:gap-8 px-3 py-4 sm:px-6 sm:py-8 bg-gradient-to-t from-white/50 to-[#F4F0FD]">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[12px] font-semibold tracking-wide text-[#6E7B86]">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 sm:h-10 sm:w-10">
                 <img src={datapricing} alt="" className="h-5 w-5 sm:h-7 sm:w-7" />
                </span>
                <span className="text-[11px] uppercase sm:text-[14px]">{heroTitle}</span>
              </div>

              <h1 className="mt-3 whitespace-pre-line text-[22px] font-semibold leading-[1.08] tracking-tight text-[#24303A] sm:mt-4 sm:text-[24px] md:text-[28px]">
                {heroSubTitle}
              </h1>

              <p className="mt-3 max-w-2xl text-[14px] leading-6 text-[#6E7B86] sm:mt-4 sm:text-[16px]">
                {heroDescription}
              </p>
            </div>

            <div className="flex items-center justify-center ">
                <div className="relative mx-auto flex h-[200px] items-center justify-center sm:h-[200px] w-[200px]">
                    <img
                      src={heroImage || ''}
                      alt=""
                      className="h-[70px] w-[70px] object-contain sm:h-[200px] sm:w-[200px]"
                      onError={(e) => {
                        e.currentTarget.src = privacy;
                      }}
                    />
                </div>
            </div>
          </div>

          <div className=" border-[#E6EAF0] bg-white pt-3 sm:pt-6 md:pt-8 px-3 py-4 sm:px-4 sm:py-6">
            <h2 className="text-center text-[18px] font-semibold text-[#24303A] sm:text-[20px]">What we collect and why</h2>

            <div className="mt-6 grid gap-8 grid-cols-1">
              {loading ? (
                Array.from({ length: 4 }).map((_, idx) => (
                  <div key={idx} className="h-[132px] rounded-2xl border border-[#E6EAF0] bg-white/60" />
                ))
              ) : featureItems.length ? (
                featureItems.map((it) => (
                  <InfoRow
                    key={it?.id ?? it?.title}
                    icon={
                      isHttpUrl(it?.image_url) ? (
                        <img
                          src={it.image_url}
                          alt=""
                          className="h-full w-full object-contain"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <SectionIcon title={it?.title} sizeClass="h-16 w-16" />
                      )
                    }
                    title={normalizeText(it?.title)}
                    description={normalizeText(it?.description)}
                  />
                ))
              ) : (
                <p className="col-span-full text-center text-[16px] text-[#6E7B86]">
                  {error ? error : "No privacy policy sections have been configured yet."}
                </p>
              )}
            </div>

            <div className="mt-10 sm:mt-14">
              <h2 className="text-center text-[18px] font-semibold text-[#24303A] sm:text-[20px]">Your choices and controls</h2>
              <p className="mt-2 text-center text-[14px] font-normal leading-6 text-[#6E7B86] sm:text-[16px]">Manage your preferences anytime.</p>

              <div className="mt-5 grid gap-4 sm:gap-6 md:mt-6 md:grid-cols-3">
                {loading ? (
                  Array.from({ length: 3 }).map((_, idx) => (
                    <div key={idx} className="h-[200px] rounded-2xl border-2 border-[#E6EAF0] bg-white" />
                  ))
                ) : policyItems.length ? (
                  policyItems.map((it, idx) => (
                    <InfoRowCentered
                      key={it?.id ?? it?.title}
                      icon={
                        isHttpUrl(it?.image_url) ? (
                          <img
                            src={it.image_url}
                            alt=""
                            className="h-full w-full object-contain"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <SectionIcon title={it?.title} sizeClass="h-20 w-20" />
                        )
                      }
                      title={normalizeText(it?.title)}
                      description={normalizeText(it?.description)}
                      className={
                        idx === 0
                          ? "bg-[#F2F8FF] border-[#2EB5C980]"
                          : idx === 1
                            ? "bg-[#F59E0B0D] border-[#F59E0B80]"
                            : "bg-[#2FC98D1A] border-[#2FC98D80]"
                      }
                    />
                  ))
                ) : (
                  <p className="col-span-full text-center text-[16px] text-[#6E7B86]">
                    {error ? error : "No choices and controls have been configured yet."}
                  </p>
                )}
              </div>
            </div>

            <div className="pt-10 sm:pt-14 md:pt-20">
              <div className="rounded-2xl border border-[#E6EAF0] bg-[#F5F2FF] px-4 py-4 sm:px-6 sm:py-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                   <img src={privacy} className="h-9 w-9 sm:h-11 sm:w-11" alt="" />
                  <div className="min-w-0">
                    <h3 className="text-[16px] font-semibold text-[#7352C7] sm:text-[18px]">Questions about your privacy?</h3>
                    <p className="mt-1 text-[14px] font-normal leading-5 text-[#6E7B86] sm:text-[16px]">
                      We&apos;re here to help. Email us anytime at{" "}
                      <a
                        className="font-semibold text-[#7352C7] underline underline-offset-2"
                        href="mailto:privacy@femtyz.co.uk"
                      >
                        admin@femtyz.com
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center justify-center gap-3 text-center sm:mt-8 sm:flex-row sm:gap-10">
                <p className="text-[14px] font-medium text-[#7352C7] sm:text-[16px]">
                  Your trust is our priority. Always.
                </p>
                <div className="pointer-events-none text-[#7352C7]">
                <svg className="h-8 w-16" viewBox="0 0 64 24" fill="none" aria-hidden="true">
                      <path
                        d="M18 6c-6 0-10 4-10 6s4 6 10 6c10 0 18-12 28-12 6 0 10 4 10 6s-4 6-10 6c-10 0-18-12-28-12Z"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

